import MatchCard from './MatchCard';
import { ProductTypeDataName } from 'enums/product-type';
import { useEffect, useState } from 'react';
import MatchCardSkeleton from './skeletons/MatchCardSkeleton';
import { useQueryGetMatches } from 'modules/Business/services/matches/queries';
import PropTypes from 'prop-types';
import useMatchesActions from './hooks/useMatchesActions';
import { useParams } from 'react-router-dom';
import * as fundingSearchActions from 'reducers/fundingSearch/reducer';
import { connect } from 'react-redux';
import { Box, IconClear, IconDone, useLayout } from '@capitalise/ui-design-system';
import AlertBox from 'modules/Business/components/Funding/AlertBox';
import Toast from 'modules/Business/components/Toast';
import { statusIntoIndexes, stepsToHide } from 'modules/Business/components/Funding/MatchStepper';
import { events } from 'enums/sse';
import { useSSEEntityEvent } from 'services/sse/queries';
import { useQueryGetProfileByFundingSearchId } from 'services/case/queries';
import { useAuthContext } from 'HOC/withAuth';
import LenderSelectionModal from 'modules/FundingSearchAlpha/containers/LenderSelectionModal';
import IncompleteDriver from './IncompleteDriver';
import { FundingMatchStatus, FundingMatchStatusLabel } from 'modules/Business/shared/types';

export const Matches = ({
  requestUpdateFundingSearchMatches,
  statusLabel,
  onGoToApplicationClick,
}) => {
  const { fundingSearchId } = useParams();
  const { renderToaster } = useLayout();
  const [lenderSelectionOpen, setLenderSelectionOpen] = useState(false);
  const { user } = useAuthContext();
  const { data: profile } = useQueryGetProfileByFundingSearchId(fundingSearchId);

  const {
    data: matches,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQueryGetMatches(fundingSearchId);

  useSSEEntityEvent({
    user: !!fundingSearchId,
    eventType: events.matchResultUpdate,
    finishCallback: refetch,
    rateLimitInMilliseconds: 3000,
  });

  const sortedMatches = matches
    ?.map((match) => ({
      ...match,
      order: (stepsToHide.indexOf(match.status) >= 0 ? 0 : statusIntoIndexes[match.status]) || -1,
    }))
    .sort((a, b) => b.order - a.order);

  const hasInstantOffers = !!sortedMatches?.some((match) => !!match.canUseInstantOffer);
  const hasIncompletedApplication =
    statusLabel === FundingMatchStatusLabel[FundingMatchStatus.incomplete];

  const onApiFinish =
    (type = 'success') =>
    (message) => {
      const icon = type === 'success' ? IconDone : IconClear;

      renderToaster(<Toast text={message} icon={icon} />);
    };

  const { onActionClick } = useMatchesActions(
    fundingSearchId,
    refetch,
    requestUpdateFundingSearchMatches,
    onApiFinish(),
    onApiFinish('error'),
    setLenderSelectionOpen,
  );

  useEffect(() => {
    if (fundingSearchId) {
      requestUpdateFundingSearchMatches(fundingSearchId);
    }
  }, [fundingSearchId]);

  return hasIncompletedApplication ? (
    <Box mb={{ _: '2xlarge', md: '0' }}>
      <IncompleteDriver hasIntantOffers={hasInstantOffers} onClick={onGoToApplicationClick} />
    </Box>
  ) : (
    <>
      <AlertBox
        description="Failed to get funding matches, please try again later"
        show={!!error}
        variant="danger"
      />
      <Box paddingTop={{ md: '2xlarge', default: 'xsmall' }}>
        {!isLoading &&
          !isFetching &&
          sortedMatches?.map((match, index) => {
            const hasOffer = Object.keys(match.offer).length > 0;
            const product = match.offer?.productType || match.products[0];
            return (
              <MatchCard
                {...(hasOffer && { ...match.offer[ProductTypeDataName[product]] })}
                {...(hasOffer && { documents: match.offer.documents })}
                {...(hasOffer && { publicComment: match.offer.publicComment })}
                {...(hasOffer && { offerId: match.offer.id })}
                key={match.id}
                id={match.id}
                logo={match.lender.logo}
                name={match.lender.companyName}
                product={product}
                status={match.status}
                matchCompletion={match.matchCompletion}
                hasOffer={hasOffer}
                hasInstantOffer={match.canUseInstantOffer}
                lender={match.lender.companyName}
                onDropdownClick={onActionClick(match.id, match.lender.companyName, match.offer?.id)}
                index={index}
                statusComment={match.statusComment}
              />
            );
          })}

        <LenderSelectionModal
          open={lenderSelectionOpen}
          onClose={() => setLenderSelectionOpen(false)}
          profile={profile}
          fundingSearch={{ id: fundingSearchId }}
          userType={user?.userType}
        />

        {(isLoading || isFetching) && (
          <>
            <MatchCardSkeleton index={1} />
            <MatchCardSkeleton index={2} />
            <MatchCardSkeleton index={3} />
          </>
        )}
      </Box>
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  requestUpdateFundingSearchMatches: (fundingSearchId) =>
    dispatch(fundingSearchActions.requestFundingSearchMatches(fundingSearchId)),
});

Matches.propTypes = {
  requestUpdateFundingSearchMatches: PropTypes.func,
  statusLabel: PropTypes.string,
  onGoToApplicationClick: PropTypes.func,
};

export default connect(undefined, mapDispatchToProps)(Matches);
